import { useInterpret } from '@xstate/react'
import { ParseKeys } from 'i18next'
import { createContext, useLayoutEffect } from 'react'
import { createMachine, InterpreterFrom } from 'xstate'
import { DriverAttributes } from '~/shared/api'
import { DriverStatus } from '~/shared/config/enums'

type StateMeta = {
  label: ParseKeys
  color: string
  gridFilter?: boolean
}

type StateConfig = {
  [K in DriverStatus]?: Record<string, string | object> & {
    meta: StateMeta
  }
}

export const machine = createMachine({
  id: 'Driver',
  initial: DriverStatus.CREATED,
  states: {
    [DriverStatus.CHECKING]: {
      on: {
        finish_checks: {
          target: DriverStatus.CHECKS_FINISHED,
        },
        cancel_checks: {
          target: DriverStatus.CHECKS_CANCELLATION_REQUESTED,
        },
      },
      meta: {
        label: 'status.checking',
        color: 'gray',
        gridFilter: true,
      } satisfies StateMeta,
    },
    [DriverStatus.CHECKS_EXPIRED]: {
      on: {
        retry_checks: {
          target: DriverStatus.CHECKS_RETRY_REQUESTED,
        },
      },
      meta: {
        label: 'status.checks expired',
        color: 'gray',
        gridFilter: true,
      },
    },
    [DriverStatus.REJECTED]: {
      on: {
        retry_checks: {
          target: DriverStatus.CHECKS_RETRY_REQUESTED,
        },
      },
      meta: {
        label: 'status.rejected',
        color: 'outlinedRed',
        gridFilter: true,
      },
    },
    [DriverStatus.CREATED]: {
      on: {
        start_onboarding: {
          target: DriverStatus.ONBOARDING_STARTING,
        },
      },
      meta: {
        label: 'status.created',
        color: 'gray',
      },
    },
    [DriverStatus.CHECKS_STARTING]: {
      on: {
        checks_started: {
          target: DriverStatus.CHECKING,
        },
      },
      meta: {
        label: 'status.checks starting',
        color: 'gray',
      },
    },
    [DriverStatus.REJECT_IN_PROGRESS]: {
      on: {
        reject_finished: {
          target: DriverStatus.REJECTED,
        },
      },
      meta: {
        label: 'status.reject in progress',
        color: 'outlinedRed',
      },
    },
    [DriverStatus.APPROVE_IN_PROGRESS]: {
      on: {
        approve_finished: {
          target: DriverStatus.APPROVED,
        },
      },
      meta: {
        label: 'status.approve in progress',
        color: 'outlinedGreen',
      },
    },
    [DriverStatus.APPROVED]: {
      on: {
        retry_checks: {
          target: DriverStatus.CHECKS_RETRY_REQUESTED,
        },
        create_rental_contract: {
          target: DriverStatus.WORKS,
        },
      },
      meta: {
        label: 'status.approved',
        color: 'green',
        gridFilter: true,
      },
    },
    [DriverStatus.CHECKS_FINISHED]: {
      on: {
        retry_checks: {
          target: DriverStatus.CHECKS_RETRY_REQUESTED,
        },
        request_additional_approval: {
          target: DriverStatus.ADDITIONAL_APPROVAL_REQUESTED,
        },
        approve: {
          target: DriverStatus.APPROVE_REQUESTED,
        },
        reject: {
          target: DriverStatus.REJECT_REQUESTED,
        },
      },
      meta: {
        label: 'status.checks finished',
        color: 'yellow',
        gridFilter: true,
      },
    },
    [DriverStatus.CHECKS_CANCELLED]: {
      on: {
        retry_checks: {
          target: DriverStatus.CHECKS_RETRY_REQUESTED,
        },
      },
      meta: {
        label: 'status.checks cancelled',
        color: 'outlinedRed',
      },
    },
    [DriverStatus.SENDING_TO_COMANDIR]: {
      on: {
        sent_to_comandir: {
          target: DriverStatus.SENT_TO_COMANDIR,
        },
      },
      meta: {
        label: 'status.sending to comandir',
        color: 'gray',
      },
    },
    [DriverStatus.SENT_TO_COMANDIR]: {
      on: {
        approve: {
          target: DriverStatus.APPROVE_REQUESTED,
        },
        reject: {
          target: DriverStatus.REJECT_REQUESTED,
        },
      },
      meta: {
        label: 'status.sent to comandir',
        color: 'brand',
        gridFilter: true,
      },
    },
    [DriverStatus.CHECKS_CANCELLATION_REQUESTED]: {
      on: {
        checks_cancelled: {
          target: DriverStatus.CHECKS_CANCELLED,
        },
      },
      meta: {
        label: 'status.checks cancellation requested',
        color: 'gray',
      },
    },
    [DriverStatus.ONBOARDING_STARTING]: {
      on: {
        onboarding_started: {
          target: DriverStatus.ONBOARDING_STARTED,
        },
      },
      meta: {
        label: 'status.onboarding starting',
        color: 'gray',
      },
    },
    [DriverStatus.ONBOARDING_STARTED]: {
      on: {
        start_checking: {
          target: [DriverStatus.CHECKS_STARTING],
        },
      },
      meta: {
        label: 'status.onboarding started',
        color: 'gray',
      },
    },
    [DriverStatus.REJECT_REQUESTED]: {
      on: {
        reject_started: {
          target: DriverStatus.REJECT_IN_PROGRESS,
        },
      },
      meta: {
        label: 'status.reject requested',
        color: 'gray',
      },
    },
    [DriverStatus.APPROVE_REQUESTED]: {
      on: {
        approve_started: {
          target: DriverStatus.APPROVE_IN_PROGRESS,
        },
      },
      meta: {
        label: 'status.approve requested',
        color: 'gray',
      },
    },
    [DriverStatus.CHECKS_RETRY_REQUESTED]: {
      on: {
        start_checking: {
          target: DriverStatus.CHECKS_STARTING,
        },
      },
      meta: {
        label: 'status.checks retry requested',
        color: 'gray',
      },
    },
    [DriverStatus.ADDITIONAL_APPROVAL_REQUESTED]: {
      on: {
        send_to_comandir: {
          target: DriverStatus.SENDING_TO_COMANDIR,
        },
      },
      meta: {
        label: 'status.additional approval requested',
        color: 'gray',
      },
    },
    [DriverStatus.WORKS]: {
      on: {
        create_rental_contract: {
          target: DriverStatus.WORKS,
        },
      },
      meta: {
        label: 'status.works',
        color: 'green',
        gridFilter: true,
      },
    },
    [DriverStatus.FIRED]: {
      on: {
        retry_checks: {
          target: DriverStatus.CHECKS_RETRY_REQUESTED,
        },
        create_rental_contract: {
          target: DriverStatus.WORKS,
        },
      },
      meta: {
        label: 'status.fired',
        color: 'red',
        gridFilter: true,
      },
    },
  } satisfies StateConfig,
  schema: {
    context: {},
    events: {} as
      | { type: 'start_checking' }
      | { type: 'start_onboarding' }
      | { type: 'reject_started' }
      | { type: 'approve_started' }
      | { type: 'send_to_comandir' }
      | { type: 'reject_finished' }
      | { type: 'onboarding_started' }
      | { type: 'checks_started' }
      | { type: 'retry_checks' }
      | { type: 'sent_to_comandir' }
      | { type: 'request_additional_approval' }
      | { type: 'approve' }
      | { type: 'approve_finished' }
      | { type: 'checks_cancelled' }
      | { type: 'reject' }
      | { type: 'finish_checks' }
      | { type: 'cancel_checks' }
      | { type: 'create_rental_contract' },
  },
  context: {},
  predictableActionArguments: true,
  preserveActionOrder: true,
})

export const useInterpreter = (driverStatus?: DriverAttributes['status']) => {
  const interpreter = useInterpret(machine)
  useLayoutEffect(() => {
    if (driverStatus) {
      interpreter.start(driverStatus)
    }
    return () => {
      interpreter.stop()
    }
  }, [interpreter, driverStatus])

  return interpreter
}

type MachineContextValue = {
  interpreter: InterpreterFrom<typeof machine>
}
export const MachineContext = createContext<MachineContextValue>(
  {} as MachineContextValue,
)
